import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, SlidersHorizontal, Map as MapIcon, List } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { SearchBar } from "@/components/SearchBar";
import { BusinessCard } from "@/components/BusinessCard";
import { ResultsMap } from "@/components/ResultsMap";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function Filters({ params, set }) {
  const sel = (key, val, items, testid) => (
    <Select value={params.get(key) || val} onValueChange={(v) => set(key, v)}>
      <SelectTrigger className="h-9 w-auto min-w-[120px] bg-white" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent className="z-[1200]">{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
  const sw = (key, label, testid) => (
    <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
      <Switch checked={params.get(key) === "true"} onCheckedChange={(v) => set(key, v ? "true" : "")} data-testid={testid} />{label}
    </label>
  );
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="search-filters">
      <SlidersHorizontal className="h-4 w-4 text-slate-500" />
      {sel("sort", "rating", [["rating", "Meilleure note"], ["distance", "Plus proche"]], "filter-sort-select")}
      {sel("radius", "1", [["1", "1 km"], ["3", "3 km"], ["5", "5 km"], ["15", "15 km"], ["30", "30 km"], ["50", "50 km"]], "filter-radius-select")}
      {sel("min_rating", "0", [["0", "Toutes notes"], ["3", "3★ et +"], ["4", "4★ et +"], ["4.5", "4,5★ et +"]], "filter-rating-select")}
      {sw("in_stock", "En stock", "filter-instock-switch")}
      {sw("verified", "Pro vérifié", "filter-verified-switch")}
    </div>
  );
}

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [active, setActive] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const q = params.get("q") || "";
  const key = params.toString();

  useEffect(() => {
    if (q.length < 2) {
      return;
    }
    setLoading(true);
    setError("");
    const p = Object.fromEntries(new URLSearchParams(key));
    Object.keys(p).forEach((k) => p[k] === "" && delete p[k]);
    api.get("/search", { params: p })
      .then((r) => setData(r.data))
      .catch((e) => setError(errMsg(e)))
      .finally(() => setLoading(false));
  }, [key, q]);

  const set = (k, v) => {
    const n = new URLSearchParams(params);
    if (v && v !== "0") n.set(k, v); else n.delete(k);
    setParams(n);
  };

  const results = data?.results || [];
  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-testid="search-page">
      <SearchBar key={`${q}|${params.get("where")}`} initial={{ q, where: params.get("where") || "", lat: params.get("lat"), lon: params.get("lon") }} />
      <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
        <Filters params={params} set={set} />
        <button className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold lg:hidden" onClick={() => setShowMap(!showMap)} data-testid="toggle-map-button">
          {showMap ? <><List className="h-4 w-4" />Liste</> : <><MapIcon className="h-4 w-4" />Carte</>}
        </button>
      </div>
      {!q && <p className="py-20 text-center text-slate-500" data-testid="search-empty-prompt">Que recherchez-vous ? Un métier, un produit, une marque…</p>}
      {loading && <div className="flex items-center justify-center gap-2 py-24 text-slate-500" data-testid="search-loading"><Loader2 className="h-5 w-5 animate-spin" />Recherche dans l'annuaire officiel…</div>}
      {error && <p className="py-10 text-center text-red-600" data-testid="search-error">{error}</p>}
      {!loading && data && (
        <>
          <p className="mt-6 text-sm text-slate-600" data-testid="search-results-count">
            <b className="text-slate-900">{data.total}</b> résultat(s) pour « {q} »{data.center?.label ? ` autour de ${data.center.label}` : ""}
          </p>
          {data.radius_used > data.radius_requested && (
            <p className="mt-2 rounded-lg bg-[#FEF3C7] px-4 py-2 text-sm text-[#78350F]" data-testid="search-expanded-notice">
              Aucun résultat à {data.radius_requested} km : recherche élargie automatiquement à {data.radius_used} km.
            </p>
          )}
          <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.85fr)]">
            <div className={`min-w-0 space-y-3 ${showMap ? "hidden lg:block" : ""}`} data-testid="search-results-list">
              {results.map((b, i) => <BusinessCard key={b.id} b={b} index={i} active={active === b.id} onHover={setActive} />)}
              {results.length === 0 && <p className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500" data-testid="search-no-results">Aucun résultat. Essayez un autre terme ou élargissez le rayon.</p>}
            </div>
            <div className={`${showMap ? "block" : "hidden lg:block"}`}>
              <div className="sticky top-20 h-[70vh]"><ResultsMap items={results} center={data.center} activeId={active} /></div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
